import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { PropertiesData, Tree } from "@/types/tree";
import { configStore, documentStore } from "@/store/store";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import {
  containsBreakdanceDynamicShortcode,
  extractBreakdanceDynamicShortcodes,
  isDynamicShortcode
} from "@/lib/dynamic-data/utils";
import { treeNodeReduce } from "@/lib/tree/tree";
import { objectDeepMap } from "@/util/objects";
import { DynamicData, DynamicDataV } from "@/types/dynamic-data";
import NotificationLogger from "@/lib/notifications/notifications";
import { getFrontendUrl } from "@/lib/document";
import { getHash } from "@/util/hash";
import { getInlineBlockPreviewableItemUrl } from "@/lib/inline-editor/inline-editor";

export async function loadDynamicData(
  dynamicShortcodes: string[]
): Promise<DynamicData> {
  const overrideAjaxUrl =
    getInlineBlockPreviewableItemUrl() || getFrontendUrl();

  if (!dynamicShortcodes.length) {
    return {};
  }

  const hashedDynamicShortcodes =
    convertDynamicShortcodesToDynamicShortcodesAsHashmapWithHashedKeys(
      dynamicShortcodes
    );

  let evaluatedShortcodesFromWp: DynamicData = {};

  // if they are previewing something (i.e. editing a template) use the URL of the previewed item.
  // otherwise use the URL to the frontend
  // if the URL to the frontend doesn't exist (i.e. editing a global block), handle it the same way we
  // handle an error below
  // TODO: what should we do for dynamic data if there's nothing to preview tho?

  if (!overrideAjaxUrl) {
    Object.keys(hashedDynamicShortcodes).forEach(key => {
      evaluatedShortcodesFromWp[key] = "";
    });
    return evaluatedShortcodesFromWp;
  }

  pipe(
    await makeWordPressAjaxRequest({
      action: "breakdance_dynamic_data_get",
      overrideAjaxUrl: overrideAjaxUrl,
      dataToPost: {
        shortcodes: hashedDynamicShortcodes,
        id: documentStore.document.documentMeta.id
      },
      decoder: DynamicDataV,
      resultKey: "data"
    }),
    fold(
      errors => {
        errors.forEach(e => NotificationLogger.log.message(e.msg));
        // If there's an error return '' for all values to invalidate the cache
        // Otherwise the cache will become empty for those values and it'll keep retrying
        // To get the data forever.
        Object.keys(hashedDynamicShortcodes).forEach(key => {
          evaluatedShortcodesFromWp[key] = "";
        });
      },
      data => {
        evaluatedShortcodesFromWp = data;
      }
    )
  );

  return evaluatedShortcodesFromWp;
}

export async function loadAllDynamicDataFromTreeIntoStore() {
  const dynamicShortcodes = getAllDynamicShortcodesFromTree(
    documentStore.document.tree
  ); // TODO - what's the performance of this on a huge tree on initial load?
  const loadedDynamicData = await loadDynamicData(dynamicShortcodes);
  configStore.updateDynamicDataCache(loadedDynamicData);
}

/**
 * @param propertiesOrPropertyValue
 * @param existingDynamicDataToExclude - any keys with truthy values here wont be returned
 */
export function getDynamicShortcodesFromPropertiesData(
  propertiesOrPropertyValue: PropertiesData,
  existingDynamicDataToExclude: DynamicData
) {
  const dynamicShortcodesToFetch: string[] = [];

  // Recursively find all dynamic values
  objectDeepMap(propertiesOrPropertyValue, (value: unknown) => {
    if (isDynamicShortcode(value) && !existingDynamicDataToExclude[value]) {
      dynamicShortcodesToFetch.push(value);
    } else if (containsBreakdanceDynamicShortcode(value)) {
      const propertyShortcodes = extractBreakdanceDynamicShortcodes(
        value
      ).filter(shortcode => !existingDynamicDataToExclude[shortcode]);

      dynamicShortcodesToFetch.push(...propertyShortcodes);
    }
  });

  return dynamicShortcodesToFetch;
}

function getAllDynamicShortcodesFromTree(tree: Tree) {
  return [
    ...new Set(
      treeNodeReduce<string[]>(
        (node, acc) => {
          const dynamicShortcodes = getDynamicShortcodesFromPropertiesData(
            node.data.properties,
            configStore.dynamicDataCache.data
          );

          return [...acc, ...dynamicShortcodes];
        },
        tree.root,
        []
      )
    )
  ];
}

function convertDynamicShortcodesToDynamicShortcodesAsHashmapWithHashedKeys(
  dynamicShortcodes: string[]
): DynamicData {
  const hashmap: DynamicData = {};
  dynamicShortcodes.forEach(shortcode => {
    const hashedKey = getDynamicShortcodeHash(shortcode);
    hashmap[hashedKey] = shortcode;
  });
  return hashmap;
}

export function getDynamicShortcodeHash(shortcode: string) {
  return getHash(shortcode);
}
